// Failure Net Visualization Page
// AIAG-VDA Step 5: Failure Analysis Chain
// Visualizes Cause → Mode → Effect → Controls relationships
// ============================================================

import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../contexts/AppContext';
import { useTheme } from '../contexts/ThemeContext';
import {
    RefreshCw,
    Grid3x3,
    MousePointerClick,
    Move,
    ArrowLeft,
} from 'lucide-react';
import clsx from 'clsx';
import { PfmeaItem } from '../types';

// ============================================================
// TYPE DEFINITIONS
// ============================================================

// AIAG-VDA Failure Propagation Chain:
// Cause (4M) → Mode → Local Effect → Next-Level Effect → End Effect
type NodeKind = 'cause' | 'mode' | 'effect-local' | 'effect-next' | 'effect-end' | 'control-prev' | 'control-det';

interface D3Node extends d3.SimulationNodeDatum {
    id: string;
    kind: NodeKind;
    text: string;
    fourM?: string; // For cause nodes: Material, Method, Machine, Man
    w: number;
    h: number;
    x?: number;
    y?: number;
    vx?: number;
    vy?: number;
    fx?: number | null;
    fy?: number | null;
}

interface D3Link extends d3.SimulationLinkDatum<D3Node> {
    source: string | D3Node;
    target: string | D3Node;
    color: string;
    dash?: string;
}

const NODE_TYPE_MAP: Record<NodeKind, string> = {
    cause: 'Failure Cause (4M)',
    mode: 'Failure Mode',
    'effect-local': 'Local Effect',
    'effect-next': 'Next-Level Effect',
    'effect-end': 'End Effect (Customer)',
    'control-prev': 'Prevention Control',
    'control-det': 'Detection Control',
};

const FailureNetPage: React.FC = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const { activeProjectName, pfmeaItems: contextPfmeaItems } = useAppContext();
    const { theme } = useTheme();
    const navigate = useNavigate();
    const isDark = theme === 'dark';

    const [layoutMode, setLayoutMode] = useState<'grid' | 'force'>('grid');
    const [focusedId, setFocusedId] = useState<string | null>(null);
    const [highlightedIds, setHighlightedIds] = useState<Set<string>>(new Set());
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    // Use real PFMEA data from context, fallback to example if empty
    const pfmeaItems: PfmeaItem[] = useMemo(() => {
        // If we have real data, use it
        if (contextPfmeaItems.length > 0) {
            return contextPfmeaItems;
        }
        // Fallback: example data for demonstration when no real data exists
        return [
            {
                id: 'demo-1',
                projectName: activeProjectName || 'Demo',
                standard: 'AIAG_VDA' as const,
                processStepNumber: 'OP-020',
                processStepName: 'Spot Welding',
                processElement: 'Machine',
                processFunction: 'Join metal sheets',
                requirement: 'Weld strength ≥ 500 MPa',
                failureMode: 'Weak weld penetration',
                failureEffectLocal: 'Reduced joint strength at weld point',
                failureEffectNext: 'Assembly structural weakness',
                failureEffectEnd: 'Vehicle safety failure in crash test',
                failureCause: 'Insufficient welding current',
                severity: 9,
                occurrence: 4,
                detection: 6,
                actionPriority: 'H',
                rpn: 216,
                preventionControls: ['Torque wrench calibration'],
                detectionControls: ['Pull test inspection'],
                specialCharacteristic: 'CC',
                recommendedActions: [],
                status: 'approved',
                createdBy: 'demo@example.com',
                createdAt: new Date(),
                updatedAt: new Date(),
            },
        ];
    }, [contextPfmeaItems, activeProjectName]);

    // Resize Observer
    useEffect(() => {
        if (!containerRef.current) return;
        const resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                setDimensions({
                    width: entry.contentRect.width,
                    height: entry.contentRect.height
                });
            }
        });
        resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
    }, []);

    // Data Preparation - Build Node/Link Graph for AIAG-VDA Chain
    const { nodes, links } = useMemo(() => {
        const tempNodes: D3Node[] = [];
        const validNodeIds = new Set<string>();
        const measure = (text: string) => Math.min(200, Math.max(120, text.length * 6 + 24));

        // Extract unique values for each level of the chain
        const causes = new Map<string, { text: string; fourM: string }>();
        const modes = new Map<string, string>();
        const effectsLocal = new Map<string, string>();
        const effectsNext = new Map<string, string>();
        const effectsEnd = new Map<string, string>();
        const controls = new Map<string, { name: string; type: 'prevention' | 'detection' }>();

        pfmeaItems.forEach((item: PfmeaItem) => {
            // Cause (categorized by 4M)
            const causeId = `cause-${item.id}`;
            const fourM = item.processElement || ''; // Machine, Material, Method, Man
            causes.set(causeId, { text: item.failureCause || '', fourM });

            // Failure Mode
            const modeId = `mode-${item.id}`;
            modes.set(modeId, item.failureMode || '');

            // Local Effect
            const localEffectId = `effect-local-${item.id}`;
            effectsLocal.set(localEffectId, item.failureEffectLocal || '');

            // Next-Level Effect
            const nextEffectId = `effect-next-${item.id}`;
            effectsNext.set(nextEffectId, item.failureEffectNext || '');

            // End Effect (Customer Impact)
            const endEffectId = `effect-end-${item.id}`;
            effectsEnd.set(endEffectId, item.failureEffectEnd || '');

            // Controls
            (item.preventionControls || []).forEach((c: string) => {
                controls.set(`ctrl-prev-${item.id}`, { name: c || '', type: 'prevention' });
            });
            (item.detectionControls || []).forEach((c: string) => {
                controls.set(`ctrl-det-${item.id}`, { name: c || '', type: 'detection' });
            });
        });

        // Create nodes for each level
        causes.forEach((data, id) => {
            tempNodes.push({
                id,
                kind: 'cause',
                text: `[${data.fourM}] ${data.text}`,
                fourM: data.fourM,
                w: measure(data.text) + 50,
                h: 40
            });
            validNodeIds.add(id);
        });

        modes.forEach((text, id) => {
            tempNodes.push({ id, kind: 'mode', text, w: measure(text), h: 40 });
            validNodeIds.add(id);
        });

        effectsLocal.forEach((text, id) => {
            tempNodes.push({ id, kind: 'effect-local', text, w: measure(text), h: 40 });
            validNodeIds.add(id);
        });

        effectsNext.forEach((text, id) => {
            tempNodes.push({ id, kind: 'effect-next', text, w: measure(text), h: 40 });
            validNodeIds.add(id);
        });

        effectsEnd.forEach((text, id) => {
            tempNodes.push({ id, kind: 'effect-end', text, w: measure(text), h: 40 });
            validNodeIds.add(id);
        });

        controls.forEach((ctrl, id) => {
            const kind: NodeKind = ctrl.type === 'prevention' ? 'control-prev' : 'control-det';
            tempNodes.push({ id, kind, text: ctrl.name, w: measure(ctrl.name), h: 40 });
            validNodeIds.add(id);
        });

        // Create links for the AIAG-VDA chain
        const tempLinks: D3Link[] = [];
        const addLink = (source: string, target: string, color: string, dash?: string) => {
            if (validNodeIds.has(source) && validNodeIds.has(target)) {
                tempLinks.push({ source, target, color, dash });
            }
        };

        pfmeaItems.forEach((item: PfmeaItem) => {
            const causeId = `cause-${item.id}`;
            const modeId = `mode-${item.id}`;
            const localEffectId = `effect-local-${item.id}`;
            const nextEffectId = `effect-next-${item.id}`;
            const endEffectId = `effect-end-${item.id}`;
            const prevCtrlId = `ctrl-prev-${item.id}`;
            const detCtrlId = `ctrl-det-${item.id}`;

            // Main AIAG-VDA Chain (left to right)
            addLink(causeId, modeId, '#f44336');           // Red: Cause → Mode
            addLink(modeId, localEffectId, '#ff5722');     // Deep Orange: Mode → Local Effect
            addLink(localEffectId, nextEffectId, '#ff9800'); // Orange: Local → Next
            addLink(nextEffectId, endEffectId, '#ff6f00');  // Amber: Next → End

            // Prevention Controls (link to Cause)
            if (validNodeIds.has(prevCtrlId)) {
                addLink(prevCtrlId, causeId, '#4caf50'); // Green: Control prevents Cause
            }

            // Detection Controls (link to Mode)
            if (validNodeIds.has(detCtrlId)) {
                addLink(detCtrlId, modeId, '#2196f3', '4,4'); // Blue dashed: Control detects Mode
            }
        });

        return { nodes: tempNodes, links: tempLinks };
    }, [pfmeaItems]);

    // Calculate highlighted IDs based on focused node
    useEffect(() => {
        if (!focusedId) {
            setHighlightedIds(new Set());
            return;
        }

        const relatedIds = new Set<string>([focusedId]);

        // Find all connected nodes (upstream and downstream)
        const traverse = (nodeId: string, direction: 'both' | 'source' | 'target') => {
            links.forEach(link => {
                const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
                const targetId = typeof link.target === 'string' ? link.target : link.target.id;

                if ((direction === 'both' || direction === 'target') && sourceId === nodeId && !relatedIds.has(targetId)) {
                    relatedIds.add(targetId);
                    traverse(targetId, direction);
                }
                if ((direction === 'both' || direction === 'source') && targetId === nodeId && !relatedIds.has(sourceId)) {
                    relatedIds.add(sourceId);
                    traverse(sourceId, direction);
                }
            });
        };

        traverse(focusedId, 'both');
        setHighlightedIds(relatedIds);
    }, [focusedId, links]);

    // Main rendering effect
    useEffect(() => {
        if (nodes.length === 0 || dimensions.width === 0 || dimensions.height === 0) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        const { width, height } = dimensions;
        const cy = height / 2;

        const g = svg.append('g');

        // Zoom behavior
        const zoom = d3.zoom<SVGSVGElement, unknown>()
            .scaleExtent([0.1, 4])
            .on('zoom', (event) => g.attr('transform', event.transform));

        (svg as d3.Selection<SVGSVGElement, unknown, null, undefined>).call(zoom).on("dblclick.zoom", null);

        // Arrow markers
        const defs = svg.append('defs');
        [
            { id: 'arrow-gray', color: '#90a4ae' },
            { id: 'arrow-red', color: '#f44336' },
            { id: 'arrow-orange', color: '#ff9800' },
            { id: 'arrow-green', color: '#4caf50' },
            { id: 'arrow-blue', color: '#2196f3' }
        ].forEach(({ id, color }) => {
            defs.append("marker")
                .attr("id", id)
                .attr("viewBox", "0 -5 10 10")
                .attr("refX", 10)
                .attr("refY", 0)
                .attr("markerWidth", 5)
                .attr("markerHeight", 5)
                .attr("orient", "auto")
                .append("path")
                .attr("d", "M0,-5L10,0L0,5")
                .attr("fill", color);
        });

        const linkGroup = g.append('g').attr('class', 'links');
        const nodeGroup = g.append('g').attr('class', 'nodes');

        // Grid layout for AIAG-VDA Chain
        if (layoutMode === 'grid') {
            // Calculate column positions based on node content
            const colWidth = Math.max(220, Math.min(width / 8, 300));
            const rowHeight = 80;
            // 7 columns: Prev Controls, Cause, Mode, Det Controls, Local Effect, Next Effect, End Effect
            const columnsOrder: NodeKind[] = ['control-prev', 'cause', 'mode', 'control-det', 'effect-local', 'effect-next', 'effect-end'];
            const columns = new Map(columnsOrder.map(k => [k, [] as D3Node[]]));
            nodes.forEach(n => columns.get(n.kind)?.push(n));

            const totalGridWidth = columnsOrder.length * colWidth;
            const startX = colWidth / 2 + 40; // Start from left edge with padding

            columnsOrder.forEach((kind, colIndex) => {
                const list = columns.get(kind)!;
                const xPos = startX + (colIndex * colWidth);
                const totalColHeight = list.length * rowHeight;
                const startY = Math.max(60, cy - (totalColHeight / 2));

                list.forEach((n, i) => {
                    n.x = xPos;
                    n.y = startY + (i * rowHeight);
                });
            });

            // Apply initial zoom to fit content
            const contentWidth = totalGridWidth + 100;
            const scale = Math.min(1, width / contentWidth, height / 400);
            const translateX = (width - totalGridWidth * scale) / 2 - startX * scale + 100;
            const translateY = 20;
            (svg as d3.Selection<SVGSVGElement, unknown, null, undefined>).transition().duration(750).call(zoom.transform as any, d3.zoomIdentity.translate(translateX, translateY).scale(scale));
        }

        // Draw links
        linkGroup.selectAll('line')
            .data(links)
            .join('line')
            .attr('x1', d => (nodes.find(n => n.id === (typeof d.source === 'string' ? d.source : d.source.id))?.x || 0))
            .attr('y1', d => (nodes.find(n => n.id === (typeof d.source === 'string' ? d.source : d.source.id))?.y || 0))
            .attr('x2', d => (nodes.find(n => n.id === (typeof d.target === 'string' ? d.target : d.target.id))?.x || 0))
            .attr('y2', d => (nodes.find(n => n.id === (typeof d.target === 'string' ? d.target : d.target.id))?.y || 0))
            .attr('stroke', d => d.color)
            .attr('stroke-width', d => {
                if (!focusedId) return 2.5;
                const sourceId = typeof d.source === 'string' ? d.source : d.source.id;
                const targetId = typeof d.target === 'string' ? d.target : d.target.id;
                const isHighlighted = highlightedIds.has(sourceId) && highlightedIds.has(targetId);
                return isHighlighted ? 4 : 0.4;
            })
            .attr('stroke-dasharray', d => d.dash || '')
            .attr('opacity', d => {
                if (!focusedId) return 0.9;
                const sourceId = typeof d.source === 'string' ? d.source : d.source.id;
                const targetId = typeof d.target === 'string' ? d.target : d.target.id;
                const isHighlighted = highlightedIds.has(sourceId) && highlightedIds.has(targetId);
                return isHighlighted ? 1 : 0.08;
            })
            .attr('marker-end', d => {
                const colorToMarker: Record<string, string> = {
                    '#f44336': 'url(#arrow-red)',
                    '#ff5722': 'url(#arrow-red)',
                    '#ff9800': 'url(#arrow-orange)',
                    '#ff6f00': 'url(#arrow-orange)',
                    '#4caf50': 'url(#arrow-green)',
                    '#2196f3': 'url(#arrow-blue)'
                };
                return colorToMarker[d.color] || 'url(#arrow-gray)';
            });

        // Draw nodes with distinct styling for each chain level
        const styleMap: Record<NodeKind, { fill: string, stroke: string, text: string }> = {
            'cause': { fill: '#fff3e0', stroke: '#f57c00', text: '#e65100' },        // Orange (4M)
            'mode': { fill: '#ffebee', stroke: '#d32f2f', text: '#b71c1c' },        // Red (Failure)
            'effect-local': { fill: '#fff9c4', stroke: '#f9a825', text: '#f57f17' }, // Yellow
            'effect-next': { fill: '#ffe0b2', stroke: '#fb8c00', text: '#e65100' },  // Deep Orange
            'effect-end': { fill: '#fce4ec', stroke: '#c2185b', text: '#880e4f' },   // Pink (Customer)
            'control-prev': { fill: '#e8f5e9', stroke: '#388e3c', text: '#1b5e20' }, // Green
            'control-det': { fill: '#e1f5fe', stroke: '#0288d1', text: '#01579b' },  // Blue
        };

        const nodeElements = nodeGroup.selectAll('g')
            .data(nodes, (d: any) => d.id)
            .join('g')
            .attr('transform', d => `translate(${d.x},${d.y})`)
            .style('cursor', 'pointer')
            .on('click', (event, d) => {
                event.stopPropagation();
                setFocusedId(curr => curr === d.id ? null : d.id);
            });

        nodeElements.each(function (d) {
            const el = d3.select(this);
            const style = styleMap[d.kind];

            el.append('rect')
                .attr('rx', 12)
                .attr('ry', 12)
                .attr('width', d.w)
                .attr('height', d.h)
                .attr('x', -d.w / 2)
                .attr('y', -d.h / 2)
                .attr('fill', style.fill)
                .attr('stroke', style.stroke)
                .attr('stroke-width', 2);

            el.append('text')
                .text(d.text.length > 35 ? d.text.substring(0, 32) + '...' : d.text)
                .attr('dy', 5)
                .attr('text-anchor', 'middle')
                .attr('font-size', '12px')
                .attr('pointer-events', 'none')
                .style('font-family', 'sans-serif')
                .style('fill', style.text);
        });

        // Apply highlighting
        nodeElements.style('opacity', d => !focusedId || highlightedIds.has(d.id) ? 1 : 0.1);

    }, [nodes, links, dimensions, layoutMode, focusedId, highlightedIds]);

    return (
        <div className={clsx('h-full flex flex-col', isDark ? 'bg-slate-900' : 'bg-slate-50')}>
            {/* Header */}
            <header className={clsx(
                'h-14 border-b flex items-center justify-between px-4 shrink-0',
                isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
            )}>
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/')}
                        className="p-1.5 hover:bg-slate-700 rounded"
                    >
                        <ArrowLeft size={18} />
                    </button>
                    <div>
                        <h1 className={clsx('text-lg font-bold', isDark ? 'text-white' : 'text-slate-800')}>
                            Failure Net Visualization
                        </h1>
                        <p className="text-xs text-slate-500">AIAG-VDA Step 5: Failure Analysis Chain</p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => { setFocusedId(null); setLayoutMode('grid'); }}
                        className={clsx(
                            'p-2 rounded',
                            isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-600'
                        )}
                        title="Reset View"
                    >
                        <RefreshCw size={20} />
                    </button>
                    <div className={clsx('rounded flex overflow-hidden border', isDark ? 'border-slate-700' : 'border-slate-200')}>
                        <button
                            onClick={() => setLayoutMode('grid')}
                            className={clsx(
                                'px-3 py-2 flex items-center gap-2 text-sm font-medium',
                                layoutMode === 'grid'
                                    ? 'bg-primary-500 text-white'
                                    : isDark ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-slate-100 text-slate-600'
                            )}
                        >
                            <Grid3x3 size={18} /> Grid
                        </button>
                    </div>
                </div>
            </header>

            {/* Main Canvas */}
            <div className="flex-1 bg-card relative overflow-hidden" ref={containerRef}>
                <svg ref={svgRef} className={clsx('w-full h-full cursor-move', isDark ? 'bg-slate-900/50' : 'bg-slate-50')}></svg>

                {/* Instructions */}
                <div className={clsx(
                    'absolute top-4 left-4 pointer-events-none p-2 rounded border text-xs flex flex-col gap-1',
                    isDark ? 'bg-slate-800/80 border-slate-700 text-slate-300' : 'bg-white/80 border-slate-200 text-slate-600'
                )}>
                    <div className="flex items-center gap-2"><MousePointerClick size={14} /> Click to Focus</div>
                    <div className="flex items-center gap-2"><Move size={14} /> Drag to Pan</div>
                </div>

                {/* Legend */}
                <div className={clsx(
                    'absolute top-4 right-4 p-3 rounded-lg border shadow-sm max-h-[280px] overflow-y-auto',
                    isDark ? 'bg-slate-800/90 border-slate-700' : 'bg-white/90 border-slate-200'
                )}>
                    <h4 className="text-xs font-bold mb-2 text-slate-500 uppercase tracking-wider">AIAG-VDA Chain</h4>
                    <div className="space-y-1.5">
                        {Object.entries(NODE_TYPE_MAP).map(([kind, text]) => {
                            const styleMap: Record<NodeKind, string> = {
                                'cause': 'bg-orange-50 border-orange-700',
                                'mode': 'bg-red-50 border-red-700',
                                'effect-local': 'bg-yellow-50 border-yellow-700',
                                'effect-next': 'bg-orange-100 border-orange-600',
                                'effect-end': 'bg-pink-50 border-pink-700',
                                'control-prev': 'bg-green-50 border-green-700',
                                'control-det': 'bg-blue-50 border-blue-700'
                            };
                            return (
                                <div key={kind} className="flex items-center gap-2">
                                    <div className={clsx('w-3 h-3 rounded border', styleMap[kind as NodeKind])}></div>
                                    <span className={clsx('text-xs', isDark ? 'text-white' : 'text-slate-800')}>{text}</span>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FailureNetPage;
